import { cn } from "@app/lib/utils";
import { usePagination } from "@design-system/hooks";
import { PaginationInfo } from "@design-system/types";
import { trans } from "@mongez/localization";
import { useSearchParams } from "@remix-run/react";
import { ChevronIcon } from "../icons";

type PaginationProps = {
  paginationInfo: PaginationInfo;
  onPageChange?: (page: number) => void;
  pushToHistory?: boolean;
  preventScrollReset?: boolean;
};

const DEFAULT_PAGINATION_INFO: PaginationInfo = {
  limit: 15,
  page: 1,
  pages: 1,
  result: 0,
  total: 0,
  currentPage: 1,
  numberOfPages: 1,
};

export function Pagination(props: PaginationProps) {
  const { pages, page } = props.paginationInfo || DEFAULT_PAGINATION_INFO;
  const [searchParams] = useSearchParams();

  const { handleNextPage, handlePageClick, handlePrevPage, pageNumbers } =
    usePagination(props);

  const currentPage = Number(searchParams.get("page")) || 1;

  if (pages < 2) return null;

  return (
    <div className="flex items-center justify-center gap-4">
      <button
        onClick={handlePrevPage}
        disabled={currentPage === 1}
        aria-label={trans("prev")}
        className="flex size-8 items-center justify-center rounded-full border transition-colors duration-300 hover:bg-bright-orange/50 disabled:opacity-30 hover:disabled:bg-transparent sm:size-12 ltr:rotate-180"
      >
        <ChevronIcon className="size-4 -rotate-90 sm:size-6" stroke="#FE4F02" />
      </button>
      {pageNumbers.map((num, index) =>
        num === -1 ? (
          <span
            key={`ellipsis-${index}`}
            className="mt-4 flex flex-row gap-1.5"
          >
            {Array.from({ length: 3 }).map((_, index) => (
              <div
                className="h-1 w-1 rounded-full bg-bright-orange opacity-70"
                key={index}
              />
            ))}
            <div className="h-1 w-1 rounded-full bg-bright-orange opacity-70"></div>
          </span>
        ) : (
          <button
            key={`page-${num}-${index}`}
            onClick={() => handlePageClick(num)}
            disabled={num === page}
            className={cn(
              "size-8 rounded-full text-xs transition-colors duration-300 hover:bg-bright-orange/50 disabled:cursor-not-allowed disabled:hover:bg-bright-orange sm:size-12 sm:text-sm",
              currentPage !== num || !currentPage
                ? "border border-bright-orange text-bright-orange"
                : "bg-bright-orange font-semibold text-white",
            )}
          >
            {num}
          </button>
        ),
      )}
      <button
        onClick={handleNextPage}
        disabled={currentPage === pages}
        aria-label={trans("next")}
        className="flex size-8 items-center justify-center rounded-full border border-ash-grey transition-colors duration-300 hover:bg-bright-orange/50 disabled:opacity-30 sm:size-12 rtl:rotate-180"
      >
        <ChevronIcon className="size-4 -rotate-90 sm:size-6" stroke="#FE4F02" />
      </button>
    </div>
  );
}
